import { MutableMap } from "helpers/MutableMap";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { getBreadcrumbs, getPath, Route } from "routes";
import { BreadcrumbsContext, UpdateRoute } from "./context";

const documentTitleBase = "SI Portal";

interface BreadcrumbsProviderProps {
	children: React.ReactNode;
}

type MutableRoute = MutableMap<Route>;

const updateCrumbs = (
	deltas: Array<UpdateRoute | string | undefined>,
	crumbs: Route[],
): Route[] => {
	if (deltas.length > crumbs.length) {
		throw new Error(`There are more title items than crumbs`);
	}

	const updatedCrumbs: MutableRoute[] = [...crumbs];
	// Get the start index
	const startIdx = crumbs.length - deltas.length;
	for (let i = startIdx; i < crumbs.length; i++) {
		const currDelta = deltas[i - startIdx]!;

		if (currDelta === undefined) {
			continue;
		}

		updatedCrumbs[i] = { ...crumbs[i]! };
		const newCrumb = updatedCrumbs[i]!;

		if (typeof currDelta === "string") {
			newCrumb.name = currDelta;
		} else {
			if (currDelta.name) {
				newCrumb.name = currDelta.name;
			}

			if (currDelta.params) {
				newCrumb.path = getPath(newCrumb, currDelta.params);
			}
		}
	}

	return updatedCrumbs;
};

const getDocumentTitle = (crumbs: Route[]): string => {
	if (crumbs.length === 1) {
		return documentTitleBase;
	}

	return `${documentTitleBase} - ${crumbs[crumbs.length - 1]!.name}`;
};

export const BreadcrumbsProvider = ({
	children,
}: BreadcrumbsProviderProps): JSX.Element => {
	const location = useLocation();

	const [breadcrumbs, setBreadcrumbs] = useState(
		getBreadcrumbs(location.pathname),
	);

	useEffect(() => {
		const newCrumbs = getBreadcrumbs(location.pathname);
		setBreadcrumbs(newCrumbs);
		document.title = getDocumentTitle(getBreadcrumbs(location.pathname));
	}, [location]);

	const updateLastBreadcrumbs = useCallback(
		(
			deltas: string | UpdateRoute | Array<UpdateRoute | string | undefined>,
		): void => {
			setBreadcrumbs((crumbs) => {
				if (crumbs.length === 0) {
					return crumbs;
				}

				const newCrumbs = updateCrumbs(
					Array.isArray(deltas) ? deltas : [deltas],
					crumbs,
				);
				document.title = getDocumentTitle(newCrumbs);
				return newCrumbs;
			});
		},
		[],
	);

	return (
		<BreadcrumbsContext.Provider value={{ breadcrumbs, updateLastBreadcrumbs }}>
			{children}
		</BreadcrumbsContext.Provider>
	);
};
